import { useEffect, useState } from "react";
import GlobalHeader from "../components/GlobalHeader";
import {
  AgentRow,
  AgentKeyRow,
  AgentKeyCreated,
  createAgent,
  listAgents,
  deleteAgent,
  createAgentKey,
  listAgentKeys,
  revokeAgentKey,
} from "../lib/api";

export default function AgentsPage() {
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New Agent modal
  const [newAgentOpen, setNewAgentOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<AgentRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Expanded key panels: agentId -> keys
  const [expandedKeys, setExpandedKeys] = useState<Record<string, AgentKeyRow[]>>({});
  const [loadingKeys, setLoadingKeys] = useState<Record<string, boolean>>({});

  // New Key modal
  const [newKeyAgentId, setNewKeyAgentId] = useState<string | null>(null);
  const [keyLabel, setKeyLabel] = useState("");
  const [issuingKey, setIssuingKey] = useState(false);
  const [issueKeyError, setIssueKeyError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<AgentKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke confirm
  const [revokeTarget, setRevokeTarget] = useState<{ agentId: string; key: AgentKeyRow } | null>(null);
  const [revoking, setRevoking] = useState(false);

  async function fetchAgents() {
    setLoading(true);
    setError(null);
    try {
      const data = await listAgents();
      setAgents(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load agents");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAgents();
  }, []);

  function openNewAgent() {
    setFormName("");
    setFormDesc("");
    setCreateError(null);
    setNewAgentOpen(true);
  }

  async function handleCreateAgent(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      await createAgent({ display_name: formName, description: formDesc || undefined });
      setNewAgentOpen(false);
      await fetchAgents();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Failed to create agent");
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteAgent() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAgent(deleteTarget.id);
      setDeleteTarget(null);
      await fetchAgents();
    } catch (e) {
      // ignore, just close
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  async function toggleKeys(agent: AgentRow) {
    if (expandedKeys[agent.id] !== undefined) {
      // collapse
      setExpandedKeys((prev) => {
        const next = { ...prev };
        delete next[agent.id];
        return next;
      });
      return;
    }
    setLoadingKeys((prev) => ({ ...prev, [agent.id]: true }));
    try {
      const keys = await listAgentKeys(agent.id);
      setExpandedKeys((prev) => ({ ...prev, [agent.id]: keys }));
    } finally {
      setLoadingKeys((prev) => ({ ...prev, [agent.id]: false }));
    }
  }

  async function refreshKeys(agentId: string) {
    const keys = await listAgentKeys(agentId);
    setExpandedKeys((prev) => ({ ...prev, [agentId]: keys }));
  }

  function openIssueKey(agentId: string) {
    setNewKeyAgentId(agentId);
    setKeyLabel("");
    setIssueKeyError(null);
    setCreatedKey(null);
    setCopied(false);
  }

  async function handleIssueKey(e: React.FormEvent) {
    e.preventDefault();
    if (!newKeyAgentId) return;
    setIssuingKey(true);
    setIssueKeyError(null);
    try {
      const result = await createAgentKey(newKeyAgentId, keyLabel || undefined);
      setCreatedKey(result);
      await refreshKeys(newKeyAgentId);
    } catch (e) {
      setIssueKeyError(e instanceof Error ? e.message : "Failed to issue key");
    } finally {
      setIssuingKey(false);
    }
  }

  async function handleRevokeKey() {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeAgentKey(revokeTarget.agentId, revokeTarget.key.id);
      await refreshKeys(revokeTarget.agentId);
      setRevokeTarget(null);
    } catch {
      setRevokeTarget(null);
    } finally {
      setRevoking(false);
    }
  }

  function copyKey(key: string) {
    navigator.clipboard.writeText(key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <GlobalHeader activeTab="agents" />

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            My Agents
          </h1>
          <button
            onClick={openNewAgent}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            New Agent
          </button>
        </div>

        {loading && (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5 animate-pulse h-24"
              />
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && agents.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            No agents yet. Create your first agent!
          </p>
        )}

        {!loading && !error && agents.length > 0 && (
          <div className="space-y-4">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden"
              >
                {/* Agent card header */}
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {agent.display_name}
                      </h2>
                      {agent.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                          {agent.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 dark:text-gray-500">
                        <span>{agent.total_commits} commits</span>
                        <span>{agent.total_rejections} rejections</span>
                        <span>Created {formatDate(agent.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4 shrink-0">
                      <button
                        onClick={() => toggleKeys(agent)}
                        className="px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        {expandedKeys[agent.id] !== undefined ? "Hide Keys" : "Manage Keys"}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(agent)}
                        className="px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>

                {/* Keys panel */}
                {loadingKeys[agent.id] && (
                  <div className="border-t border-gray-100 dark:border-gray-800 px-5 py-4">
                    <div className="animate-pulse h-8 bg-gray-100 dark:bg-gray-800 rounded" />
                  </div>
                )}

                {expandedKeys[agent.id] !== undefined && (
                  <div className="border-t border-gray-100 dark:border-gray-800 px-5 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        API Keys
                      </h3>
                      <button
                        onClick={() => openIssueKey(agent.id)}
                        className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        Issue New Key
                      </button>
                    </div>

                    {expandedKeys[agent.id].length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        No API keys yet.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {expandedKeys[agent.id].map((key) => (
                          <div
                            key={key.id}
                            className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs ${
                              key.revoked_at
                                ? "bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-600"
                                : "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">
                                {key.label ?? "(no label)"}
                              </span>
                              {key.revoked_at && (
                                <span className="ml-2 text-red-400 dark:text-red-500">
                                  Revoked {formatDate(key.revoked_at)}
                                </span>
                              )}
                              <div className="flex gap-3 mt-0.5 text-gray-400 dark:text-gray-500">
                                <span>Created {formatDate(key.created_at)}</span>
                                <span>Last used {formatDate(key.last_used_at)}</span>
                              </div>
                            </div>
                            {!key.revoked_at && (
                              <button
                                onClick={() => setRevokeTarget({ agentId: agent.id, key })}
                                className="ml-3 shrink-0 px-2 py-1 text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 border border-red-200 dark:border-red-800 rounded transition-colors"
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* New Agent Modal */}
      {newAgentOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
              New Agent
            </h2>
            <form onSubmit={handleCreateAgent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  placeholder="My Agent"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  rows={3}
                  placeholder="Optional description"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {createError && (
                <p className="text-red-600 dark:text-red-400 text-sm">{createError}</p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setNewAgentOpen(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {creating ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Issue New Key Modal */}
      {newKeyAgentId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            {createdKey ? (
              <>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                  API Key Created
                </h2>
                <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-4 py-3 text-sm text-yellow-700 dark:text-yellow-400">
                  This key will not be shown again. Copy it now.
                </div>
                <div className="flex items-center gap-2 mb-6">
                  <code className="flex-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 font-mono break-all">
                    {createdKey.key}
                  </code>
                  <button
                    onClick={() => copyKey(createdKey.key)}
                    className="shrink-0 px-3 py-2 text-xs font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setNewKeyAgentId(null)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                  Issue New API Key
                </h2>
                <form onSubmit={handleIssueKey} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Label
                    </label>
                    <input
                      type="text"
                      value={keyLabel}
                      onChange={(e) => setKeyLabel(e.target.value)}
                      placeholder="e.g. production"
                      className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {issueKeyError && (
                    <p className="text-red-600 dark:text-red-400 text-sm">{issueKeyError}</p>
                  )}

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setNewKeyAgentId(null)}
                      className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={issuingKey}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      {issuingKey ? "Issuing..." : "Issue Key"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Agent Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
              Delete Agent
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {deleteTarget.display_name}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAgent}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Key Confirm */}
      {revokeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
              Revoke API Key
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to revoke the key{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {revokeTarget.key.label ?? "(no label)"}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRevokeTarget(null)}
                disabled={revoking}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokeKey}
                disabled={revoking}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {revoking ? "Revoking..." : "Revoke"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
